import React from 'react';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Globe } from 'lucide-react';
import { cn } from 'lib/utils';
import { ieltsHeadline, scopeLabel } from '../../constants';
import type { ExamReport, IeltsBandReport, SatScoreReport } from '../../types';
import { GradingPendingStatus } from './GradingPendingStatus';
import { IeltsCriteriaBreakdown } from './IeltsCriteriaBreakdown';
import { LockedInsight, useExamInsights } from 'modules/examInsights';
import { BigFigure, Chip, Eyebrow, HeroTile, MeterRow, RESULT_ACCENTS, ResultCard, ResultHero, formatDelta, toneForPercent, type AttemptDelta } from 'modules/resultView';

interface Props {
  report: ExamReport;
  className?: string;
  /** Re-reads the report while a Writing or Speaking band is still being checked. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** The learner's goal for this exam ("7.5", "1450"), shown as a chip and as the gap left. */
  target?: number | null;
  /** How this attempt moved from the learner's previous one of the same test. */
  delta?: AttemptDelta | null;
  /** Narrow column (the public result card): the hero stacks at every width. */
  compact?: boolean;
  /** Shown between the score and the details: the unlock banner while the analysis is locked. */
  afterHero?: React.ReactNode;
}

/** Estimated IELTS band report (TRF-style) or Digital SAT score report. */
export function ScaledReportCard({ report, className, onRefresh, refreshing, target, delta, afterHero, compact }: Props) {
  return report.standard === 'IELTS' ? (
    <IeltsReport report={report} className={className} onRefresh={onRefresh} refreshing={refreshing} target={target} delta={delta} afterHero={afterHero} compact={compact} />
  ) : (
    <SatReport report={report} className={className} target={target} delta={delta} afterHero={afterHero} compact={compact} />
  );
}

const band = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(1) : '—');
const TOP_BAND = 9;
const bandPercent = (value: number | null | undefined) => (typeof value === 'number' ? (value / TOP_BAND) * 100 : 0);
const formatGap = (n: number) => String(Math.round(n * 100) / 100);

function HeroNote({ children }: { children: React.ReactNode }) {
  return <p className="mt-3.5 text-[13px] leading-relaxed text-[#364055] lg:text-[13.5px]">{children}</p>;
}

/** Where a full mock stands while Writing or Speaking is still being graded: the sections done, the ones waiting. */
function GradingProgress({ report }: { report: IeltsBandReport }) {
  const { t } = useTranslation();
  const steps = [
    report.listening && { key: 'listening', label: t('exam.kind.listening'), done: true },
    report.reading && { key: 'reading', label: t('exam.kind.reading'), done: true },
    report.writing && { key: 'writing', label: t('exam.kind.writing'), done: !report.writing.pending },
    report.speaking && { key: 'speaking', label: t('exam.kind.speaking'), done: !report.speaking.pending },
  ].filter(Boolean) as Array<{ key: string; label: string; done: boolean }>;
  const all = [...steps, { key: 'overall', label: t('exam.report.overall_step'), done: false }];

  return (
    <ResultCard title={t('result.view.grading_progress')}>
      <ol className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {all.map((step, i) => (
          <li key={step.key} className="flex items-center gap-2.5 text-[13px] font-semibold text-foreground">
            <span
              aria-hidden
              className={cn(
                'grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-extrabold',
                step.done ? 'bg-[#E6F4EC] text-[#166534]' : 'bg-[#F1F4FB] text-[#646D84]',
              )}
            >
              {step.done ? '✓' : step.key === 'overall' ? i + 1 : '…'}
            </span>
            <span className="min-w-0">
              {step.label}
              <span className="sr-only"> {step.done ? t('result.view.step_done') : t('result.view.step_waiting')}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-4 rounded-2xl bg-[#F1F4FB] px-4 py-3 text-[12.5px] leading-relaxed text-[#364055]">{t('result.view.grading_hint')}</p>
    </ResultCard>
  );
}

function IeltsReport({ report, className, onRefresh, refreshing, target, delta, afterHero, compact }: { report: IeltsBandReport } & Omit<Props, 'report'>) {
  const { t } = useTranslation();
  const { locked } = useExamInsights();
  const accent = RESULT_ACCENTS.ielts;
  const lead = ieltsHeadline(report);

  const label =
    lead.kind === 'OVERALL'
      ? t('exam.report.overall')
      : lead.kind === 'SKILL'
        ? t('exam.report.skill_band', { scope: scopeLabel(t, lead.scope) })
        : t('exam.report.part_score', { scope: scopeLabel(t, lead.scope) });
  const note = lead.kind === 'OVERALL' ? t('exam.report.overall_rule') : lead.kind === 'RAW' ? t('exam.report.part_note') : null;

  const raw = (skill: 'listening' | 'reading') => {
    const s = report[skill];
    return s ? t('exam.report.raw_of', { raw: s.raw, max: s.max }) : undefined;
  };

  let tiles: React.ReactNode = null;
  if (lead.kind === 'OVERALL') {
    tiles = (
      <div className="grid grid-cols-2 gap-2.5 lg:gap-3">
        {report.listening && <HeroTile label={t('exam.kind.listening')} value={band(report.listening.band)} detail={raw('listening')} percent={bandPercent(report.listening.band)} />}
        {report.reading && <HeroTile label={t('exam.kind.reading')} value={band(report.reading.band)} detail={raw('reading')} percent={bandPercent(report.reading.band)} />}
        {report.writing && (
          <HeroTile
            label={t('exam.kind.writing')}
            value={report.writing.pending ? '…' : band(report.writing.band)}
            detail={report.writing.pending ? t('exam.report.checking') : t('exam.report.two_tasks')}
            percent={bandPercent(report.writing.band)}
          />
        )}
        {report.speaking && (
          <HeroTile
            label={t('exam.kind.speaking')}
            value={report.speaking.pending ? '…' : band(report.speaking.band)}
            detail={report.speaking.pending ? t('exam.report.checking') : t('exam.report.speaking_answers', { count: report.speaking.recordings })}
            percent={bandPercent(report.speaking.band)}
          />
        )}
      </div>
    );
  } else if (lead.kind === 'SKILL' && report.writing && lead.scope.skill === 'WRITING') {
    tiles = (
      <div className="grid grid-cols-2 gap-2.5 lg:gap-3">
        {report.writing.tasks.map((task) => (
          <HeroTile
            key={task.questionId}
            label={t(task.task === 'TASK_1' ? 'exam.writing.task1' : 'exam.writing.task2')}
            value={band(task.band)}
            detail={t('exam.report.task_share', { share: task.task === 'TASK_1' ? '⅓' : '⅔', skill: t('exam.kind.writing') })}
            percent={bandPercent(task.band)}
          />
        ))}
      </div>
    );
  } else if (lead.kind === 'RAW') {
    tiles = (
      <div className="grid grid-cols-1 gap-3">
        <HeroTile
          label={t('exam.report.correct_label')}
          value={`${lead.percent}%`}
          detail={t('exam.report.correct_of', { raw: lead.raw, max: lead.max })}
          percent={lead.percent}
        />
      </div>
    );
  }

  const gap = target != null && typeof report.overall === 'number' ? Math.max(0, target - report.overall) : null;
  const hasCriteria = (report.writing?.tasks.length ?? 0) > 0 || (report.speaking?.criteria.length ?? 0) > 0;

  return (
    <section aria-label={t('exam.report.ielts_title')} className={cn('space-y-4 lg:space-y-5', className)}>
      <ResultHero accent={accent} compact={compact} label={t('exam.report.ielts_title')} watermark={<Globe className="h-full w-full" strokeWidth={0.6} />} aside={tiles}>
        <Eyebrow accent>{label}</Eyebrow>
        {lead.kind === 'RAW' ? (
          <BigFigure>
            {lead.raw}
            <span className="ml-1 text-[0.32em] font-bold tracking-normal text-[#4B5468]">/ {lead.max}</span>
          </BigFigure>
        ) : report.overall !== null ? (
          <BigFigure>{band(report.overall)}</BigFigure>
        ) : (
          <GradingPendingStatus
            className="mt-3 text-[#10182B]"
            label={lead.kind === 'OVERALL' ? t('exam.report.overall_pending') : t('exam.report.skill_pending')}
            onRefresh={onRefresh}
            refreshing={refreshing}
          />
        )}
        <div className="mt-[18px] flex flex-wrap gap-2">
          {lead.kind === 'RAW' && <Chip>{t('exam.report.percent_correct', { percent: lead.percent })}</Chip>}
          {lead.kind === 'RAW' && lead.band !== null && <Chip>{t('exam.report.band_chip', { band: band(lead.band) })}</Chip>}
          {lead.kind !== 'RAW' && report.cefr && <Chip>CEFR {report.cefr}</Chip>}
          {delta && <Chip>{t('result.view.vs_previous', { delta: formatDelta(delta) })}</Chip>}
          {lead.kind !== 'RAW' && target != null && (
            <Chip dark>
              {gap && gap > 0 ? t('exam.report.target_gap', { target, gap: formatGap(gap) }) : t('exam.report.target', { target })}
            </Chip>
          )}
        </div>
        {note && <HeroNote>{note}</HeroNote>}
        <p className="mt-3 text-[11px] leading-relaxed text-[#646D84]">{t('exam.report.disclaimer_ielts', { version: report.tableVersion })}</p>
      </ResultHero>

      {report.overall === null && lead.kind === 'OVERALL' && <GradingProgress report={report} />}

      {afterHero}

      {hasCriteria && (
        <div style={{ '--ra-fg': accent.fg } as React.CSSProperties}>
          <IeltsCriteriaBreakdown report={report} />
          {locked && (
            <LockedInsight title={t('exam_insights.criteria_title')} description={t('exam_insights.criteria_body')} lines={4} className="mt-4 lg:mt-5" />
          )}
        </div>
      )}
    </section>
  );
}

function SatReport({ report, className, target, delta, afterHero, compact }: { report: SatScoreReport; className?: string; target?: number | null; delta?: AttemptDelta | null; afterHero?: React.ReactNode; compact?: boolean }) {
  const { t } = useTranslation();
  const accent = RESULT_ACCENTS.sat;
  const { locked } = useExamInsights();
  const bySubject = (subject: 'SAT_RW' | 'SAT_MATH') => report.domains.filter((d) => d.subject === subject);
  const sections = [
    report.rw && { key: 'rw', label: t('exam.kind.sat_rw'), s: report.rw },
    report.math && { key: 'math', label: t('exam.kind.sat_math'), s: report.math },
  ].filter(Boolean) as Array<{ key: string; label: string; s: NonNullable<SatScoreReport['rw']> }>;

  // 400-1600 needs both sections; a single section leads with its own 200-800 score.
  const single = sections.length === 1 ? sections[0] : null;
  const figure = single ? single.s.scaled : report.total;
  const eyebrow = single ? single.label : t('exam.report.total');
  const gap = !single && target != null ? Math.max(0, target - report.total) : null;
  const tierLabel = (tier: 'EASY' | 'HARD' | undefined) => (tier ? t(tier === 'HARD' ? 'exam.sat.module2_hard' : 'exam.sat.module2_easy') : null);

  return (
    <section aria-label={t('exam.report.sat_title')} className={cn('space-y-4 lg:space-y-5', className)}>
      <ResultHero
        accent={accent}
        compact={compact}
        label={t('exam.report.sat_title')}
        watermark={<GraduationCap className="h-full w-full" strokeWidth={0.6} />}
        aside={
          <div className={cn('grid gap-2.5 lg:gap-3', single ? 'grid-cols-1' : 'grid-cols-2')}>
            {sections.map(({ key, label, s }) => (
              <HeroTile
                key={key}
                label={label}
                value={s.scaled}
                detail={t('exam.report.raw_of', { raw: s.raw, max: s.max })}
                percent={((s.scaled - 200) / 600) * 100}
              />
            ))}
          </div>
        }
      >
        <Eyebrow accent>{eyebrow}</Eyebrow>
        <BigFigure>{figure}</BigFigure>
        <div className="mt-[18px] flex flex-wrap gap-2">
          <Chip>{single ? t('exam.report.section_range') : t('exam.report.total_range')}</Chip>
          {delta && <Chip>{t('result.view.vs_previous', { delta: formatDelta(delta) })}</Chip>}
          {!single && target != null && <Chip dark>{gap && gap > 0 ? t('exam.report.target_gap', { target, gap }) : t('exam.report.target', { target })}</Chip>}
          {sections.map(({ key, label, s }) => {
            const tier = tierLabel(s.module2Tier as 'EASY' | 'HARD' | undefined);
            return tier ? <Chip key={key}>{`${label} · ${tier}`}</Chip> : null;
          })}
        </div>
        {single && <HeroNote>{t('exam.report.sat_single_note')}</HeroNote>}
        <p className="mt-3 text-[11px] leading-relaxed text-[#646D84]">{t('exam.report.disclaimer_sat', { version: report.tableVersion })}</p>
      </ResultHero>

      {afterHero}

      {report.domains.length === 0 && locked && (
        <LockedInsight title={t('exam_insights.domains_title')} description={t('exam_insights.domains_body')} lines={5} className="mt-0" />
      )}

      {report.domains.length > 0 && (
        <div className={cn('grid grid-cols-1 gap-4 lg:gap-5', sections.length > 1 && 'md:grid-cols-2')}>
          {(['SAT_RW', 'SAT_MATH'] as const).map((subject) =>
            bySubject(subject).length ? (
              <ResultCard
                key={subject}
                accent={accent}
                title={t('exam.report.domains_of', { subject: t(subject === 'SAT_RW' ? 'exam.kind.sat_rw' : 'exam.kind.sat_math') })}
              >
                <div className="mt-2">
                  {bySubject(subject).map((d) => {
                    const percent = d.total ? Math.round((d.correct / d.total) * 100) : 0;
                    return (
                      <MeterRow
                        key={d.domain}
                        label={d.domain === 'Other' ? t('exam.report.other_domain') : d.domain}
                        value={`${d.correct} / ${d.total}`}
                        percent={percent}
                        color={toneForPercent(percent, 80, 60)}
                      />
                    );
                  })}
                </div>
              </ResultCard>
            ) : null,
          )}
        </div>
      )}
    </section>
  );
}

export default ScaledReportCard;
